import React, { useMemo, useState } from 'react';
import { FileBarChart, Download } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  TermFilter,
  summarizeMetrics,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';

const Reports: React.FC = () => {
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    return summarizeMetrics(records);
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Reports</h1>
          <p className="text-sm text-gray-500">Download and view student development reports</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <InfoBox label="Total Students" value={`${stats.totalStudents}`} />
        <InfoBox label="Students Analyzed" value={`${stats.totalStudents}`} />
        <InfoBox label="Average Competency" value={`${stats.avgCompetency}%`} />
        <InfoBox label="Needs Support" value={`${stats.needsSupportCount}`} />
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ReportCard title="Holistic Development Summary" desc="Complete overview of all development dimensions" />
        <ReportCard title="Behavior & Peer Report" desc="Behavior scores and peer interaction analysis" />
        <ReportCard title="Academic Competency Report" desc="Term-wise competency comparison" />
        <ReportCard title="Parent Engagement Report" desc="Parent feedback and involvement insights" />
        <ReportCard title="Teacher Observation Report" desc="Strength/challenge tags and recommendations" />
        <ReportCard title="Physical Health Report" desc="Height, weight, BMI trends" />
      </div>
    </div>
  );
};

const InfoBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-gray-900">{value}</p>
  </div>
);

const ReportCard: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition cursor-pointer">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 bg-indigo-50 rounded-lg">
        <FileBarChart size={18} className="text-indigo-600" />
      </div>
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    <p className="text-xs text-gray-500 mb-4">{desc}</p>
    <button className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition">
      <Download size={13} /> Download Report
    </button>
  </div>
);

export default Reports;
